import {Tag} from 'antd'
import React, {ReactNode, useLayoutEffect, useState} from 'react'

const SEPARATOR = ', '

const PROBE_CLASS = 'multi-value-tag--probe'

// Greedily fills one tag after another: a value only moves on to the next tag
// once the current one has no room left for it, so a value is never broken up.
//
// Every candidate is measured on a real tag rather than calculated, which is
// what makes the answer survive things a calculation would have to guess at -
// the web font, the icon, the padding the theme happens to give a tag.
const packValues = (values: string[], suffix: string, row: HTMLElement, probe: HTMLElement) => {
  const maxWidth = row.clientWidth
  const text = probe.querySelector('.multi-value-tag__probe-text')
  if (!maxWidth || !text) {
    return [values]
  }

  const fits = (candidate: string) => {
    text.textContent = candidate
    return probe.getBoundingClientRect().width <= maxWidth
  }

  const groups: string[][] = []
  let current: string[] = []

  values.forEach((value, index) => {
    // The suffix trails the last value, so it is the last value that has to
    // find room for it as well.
    const trailer = index === values.length - 1 ? suffix : ''
    const candidate = [...current, value]

    if (current.length && !fits(candidate.join(SEPARATOR) + trailer)) {
      groups.push(current)
      current = [value]
    } else {
      current = candidate
    }
  })

  if (current.length) {
    groups.push(current)
  }

  text.textContent = ''
  return groups
}

export type MultiValueTagProps = {
  values: string[]
  icon?: ReactNode
  color?: string
  // Trails the last value, e.g. the scale the values are given on.
  suffix?: string
}

// A tag holding several values. On a wide screen that is the one tag it has
// always been; once the values stop fitting it becomes as many tags as it takes
// to show all of them, each carrying the icon so every one of them still says
// what it is about.
//
// The width to fit into is the tag's own parent, so the tags have to be direct
// children of the row they are laid out in.
export const MultiValueTag = ({values, icon, color, suffix = ''}: MultiValueTagProps) => {
  // The probe is held in state rather than in a ref so that the measuring below
  // re-runs against the live element whenever React hands over a new one.
  const [probeText, setProbeText] = useState<HTMLElement | null>(null)
  const [groups, setGroups] = useState<string[][]>([values])

  const valuesKey = values.join(SEPARATOR)

  useLayoutEffect(() => {
    const probe = probeText?.closest<HTMLElement>(`.${PROBE_CLASS}`)
    const row = probe?.parentElement
    if (!probe || !row) {
      return
    }

    const repack = () => setGroups(packValues(values, suffix, row, probe))
    repack()

    // The row is resized without anything remounting - by a rotation, by the
    // modal growing as the rest of the plant loads - and each time the values
    // have to be dealt out over the tags again.
    const observer = new ResizeObserver(repack)
    observer.observe(row)

    // A web font arriving after the first paint changes every width there is,
    // so what fitted while it was still the fallback font may no longer fit.
    let stale = false
    document.fonts?.ready.then(() => {
      if (!stale) {
        repack()
      }
    })

    return () => {
      stale = true
      observer.disconnect()
    }
  }, [probeText, valuesKey, suffix])

  return (
    <>
      {groups.map((group, index) => (
        <Tag
          key={index}
          icon={icon}
          color={color}
          className={
            // A tag down to a single value has nothing left to split, so from
            // here on it is the text itself that has to give way.
            group.length === 1 ? 'multi-value-tag multi-value-tag--wrapping' : 'multi-value-tag'
          }
        >
          {group.join(SEPARATOR)}
          {index === groups.length - 1 ? suffix : ''}
        </Tag>
      ))}
      {/* The tag the candidates are tried on. It is a real tag so that it
          carries the same icon and padding as the ones on show, and it is
          parked off-screen so that measuring never disturbs the row. */}
      <Tag aria-hidden icon={icon} color={color} className={`multi-value-tag ${PROBE_CLASS}`}>
        <span className="multi-value-tag__probe-text" ref={setProbeText} />
      </Tag>
    </>
  )
}
